import { supabase, getRedirectUrl } from '../supabaseClient';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { SUPABASE_FUNCTIONS_BASE_URL, SUPABASE_CONFIG } from '../../config';
import { logAccess, AccessType } from './AccessLoggerService';

// Types pour l'authentification
export type AuthUser = User;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignUpCredentials {
  email: string;
  password: string;
}

export interface AuthState {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
}

// Service d'authentification
export class AuthService {
  /**
   * Génère ou récupère un identifiant unique pour ce navigateur
   */
  static getBrowserId(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    
    const STORAGE_KEY = 'suivifinance_browser_id';
    let browserId = localStorage.getItem(STORAGE_KEY);
    
    if (!browserId) {
      // Générer un nouvel identifiant unique
      browserId = `browser_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem(STORAGE_KEY, browserId);
    }
    
    return browserId;
  }

  /**
   * Vérifie si c'est un nouveau navigateur (première connexion depuis ce navigateur)
   */
  static isNewBrowser(email: string): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    
    const STORAGE_KEY = `suivifinance_known_browsers_${email}`;
    const knownBrowsers = localStorage.getItem(STORAGE_KEY);
    const currentBrowserId = this.getBrowserId();
    
    if (!knownBrowsers) {
      // C'est la première fois qu'on se connecte depuis ce navigateur
      return true;
    }
    
    const browsers = JSON.parse(knownBrowsers);
    return !browsers.includes(currentBrowserId);
  }

  /**
   * Marque ce navigateur comme connu pour cet email
   */
  static markBrowserAsKnown(email: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    
    const STORAGE_KEY = `suivifinance_known_browsers_${email}`;
    const currentBrowserId = this.getBrowserId();
    const knownBrowsers = localStorage.getItem(STORAGE_KEY);
    
    let browsers: string[] = [];
    if (knownBrowsers) {
      browsers = JSON.parse(knownBrowsers);
    }
    
    if (!browsers.includes(currentBrowserId)) {
      browsers.push(currentBrowserId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(browsers));
    }
  }

  /**
   * Vérifie si un utilisateur a été créé il y a moins de 30 jours
   */
  static isNewUser(user: User): boolean {
    if (!user.created_at) {
      return false;
    }
    
    const createdAt = new Date(user.created_at);
    const now = new Date();
    const daysSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
    
    return daysSinceCreation < 30;
  }

  /**
   * Connexion avec email et mot de passe
   * Détecte automatiquement si c'est un nouveau navigateur et demande une vérification par email si nécessaire
   * Exception : les nouveaux utilisateurs (créés il y a moins de 30 jours) ne sont pas soumis à la vérification
   */
  static async signInWithEmail(credentials: LoginCredentials) {
    try {
      // Vérifier si c'est un nouveau navigateur
      const isNew = this.isNewBrowser(credentials.email);
      
      if (isNew) {
        // Nouveau navigateur : tenter une connexion temporaire pour vérifier la date de création
        console.log('🔒 Nouveau navigateur détecté, vérification de la date de création...');
        
        const { data: tempAuthData, error: tempError } = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        });

        if (tempError) {
          throw tempError;
        }

        // Vérifier si l'utilisateur est nouveau (créé il y a moins de 30 jours)
        if (tempAuthData?.user && this.isNewUser(tempAuthData.user)) {
          // Utilisateur nouveau : pas de vérification nécessaire, garder la session
          console.log('✅ Nouvel utilisateur détecté (moins de 30 jours), connexion directe autorisée');
          
          // Marquer ce navigateur comme connu
          this.markBrowserAsKnown(credentials.email);

          // Enregistrer la connexion dans l'historique
          logAccess(AccessType.LOGIN, `Connexion réussie avec l'email: ${credentials.email}`).catch(console.error);

          return { data: tempAuthData, error: null };
        } else {
          // Utilisateur ancien : déconnecter et demander la vérification
          console.log('🔒 Utilisateur ancien détecté, déconnexion et demande de vérification...');
          await supabase.auth.signOut();
          
          const { error: codeError } = await this.sendLoginVerificationCode(credentials.email);
          
          if (codeError) {
            throw new Error(codeError);
          }
          
          // Retourner un code spécial pour indiquer qu'une vérification est nécessaire
          return { 
            data: null, 
            error: null,
            requiresVerification: true,
            message: 'Code de vérification envoyé par email'
          };
        }
      }
      
      // Navigateur connu : connexion normale
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        throw error;
      }

      // Marquer ce navigateur comme connu après une connexion réussie
      this.markBrowserAsKnown(credentials.email);

      // Enregistrer la connexion dans l'historique
      logAccess(AccessType.LOGIN, `Connexion réussie avec l'email: ${credentials.email}`).catch(console.error);

      return { data, error: null };
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Inscription avec email et mot de passe
   */
  static async signUpWithEmail(credentials: SignUpCredentials) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          emailRedirectTo: getRedirectUrl(),
        },
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Renvoyer l'email de vérification d'inscription
   */
  static async resendVerificationEmail(email: string) {
    try {
      const { data, error } = await (supabase.auth as any).resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: getRedirectUrl(),
        },
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email de vérification:", error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Connexion avec Google
   */
/**
   * Connexion avec Google
   */
static async signInWithGoogle() {
  try {
    // Forcer l'utilisation de l'origine actuelle (web)
    const redirectUrl = `${window.location.origin}/auth/callback`;
    
    // Stocker l'origine d'origine dans sessionStorage pour la récupérer après redirection
    // Cela permet de détecter si Supabase redirige vers un autre domaine
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('oauth_origin', window.location.origin);
      sessionStorage.setItem('oauth_redirect_url', redirectUrl);
    }
    
    // Debug pour vérifier
    console.log('🔍 Origin:', window.location.origin);
    console.log('🔍 Redirect URL:', redirectUrl);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        skipBrowserRedirect: false,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la connexion Google:', error);
    return { data: null, error: error as AuthError };
  }
}

  /**
   * Déconnexion
   */
  static async signOut() {
    try {
      // Récupérer l'email avant la déconnexion pour l'historique
      const { data: { user } } = await supabase.auth.getUser();
      const email = user?.email || 'Unknown';

      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }

      // Enregistrer la déconnexion dans l'historique
      logAccess(AccessType.LOGOUT, `Déconnexion de l'utilisateur: ${email}`).catch(console.error);

      return { error: null };
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      return { error: error as AuthError };
    }
  }

  /**
   * Récupération de la session actuelle
   */
  static async getSession() {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erreur lors de la récupération de la session:', error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Récupération de l'utilisateur actuel
   */
  static async getCurrentUser() {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Réinitialisation du mot de passe
   */
  static async resetPassword(email: string) {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getRedirectUrl().replace('/auth/callback', '/auth/reset-password'),
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Mise à jour du mot de passe
   */
  static async updatePassword(newPassword: string) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du mot de passe:', error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Mise à jour du profil utilisateur
   */
  static async updateProfile(updates: { email?: string; data?: any }) {
    try {
      const { data, error } = await supabase.auth.updateUser(updates);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      return { data: null, error: error as AuthError };
    }
  }

  /**
   * Suppression du compte utilisateur
   */
  static async deleteAccount() {
    try {
      const { error } = await supabase.auth.admin.deleteUser(
        (await this.getCurrentUser()).data?.user?.id || ''
      );

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('Erreur lors de la suppression du compte:', error);
      return { error: error as AuthError };
    }
  }

  /**
   * Vérification si l'utilisateur est connecté
   */
  static isAuthenticated(): boolean {
    const session = supabase.auth.getSession();
    return !!session;
  }

  /**
   * Écouteur de changements d'authentification
   */
  static onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback);
  }

  /**
   * Envoie un code de réinitialisation par email
   */
  static async sendResetCode(email: string) {
    try {
      console.log('📧 Envoi du code de réinitialisation pour:', email);
      console.log('🔗 URL:', `${SUPABASE_FUNCTIONS_BASE_URL}/send_reset_code`);
      
      const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/send_reset_code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_CONFIG.ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_CONFIG.ANON_KEY}`,
        },
        body: JSON.stringify({ email }),
      });

      console.log('📡 Statut de la réponse:', response.status, response.statusText);
      
      const data = await response.json();
      console.log('📊 Données reçues:', data);

      if (!response.ok) {
        console.error('❌ Erreur de la fonction edge:', data);
        // Retourner l'erreur avec le code si disponible pour un traitement spécifique
        const errorMessage = data.error || data.details || 'Erreur lors de l\'envoi du code';
        return { data: null, error: errorMessage };
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('💥 Erreur lors de l\'envoi du code de réinitialisation:', error);
      return { data: null, error: error.message || 'Erreur lors de l\'envoi du code' };
    }
  }

  /**
   * Vérifie le code de réinitialisation
   */
  static async verifyResetCode(email: string, code: string) {
    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/verify_reset_code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_CONFIG.ANON_KEY,
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Code invalide ou expiré');
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('Erreur lors de la vérification du code:', error);
      return { data: null, error: error.message || 'Code invalide ou expiré' };
    }
  }

  /**
   * Réinitialise le mot de passe avec le code vérifié
   */
  static async resetPasswordWithCode(email: string, newPassword: string) {
    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/reset_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_CONFIG.ANON_KEY,
        },
        body: JSON.stringify({ email, new_password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la réinitialisation du mot de passe');
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);
      return { data: null, error: error.message || 'Erreur lors de la réinitialisation du mot de passe' };
    }
  }

  /**
   * Envoie un code de vérification par email pour la connexion depuis un nouveau navigateur
   */
  static async sendLoginVerificationCode(email: string) {
    try {
      console.log('📧 Envoi du code de vérification de connexion pour:', email);
      
      const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/send_login_verification_code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_CONFIG.ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_CONFIG.ANON_KEY}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Erreur de la fonction edge:', data);
        const errorMessage = data.error || data.details || 'Erreur lors de l\'envoi du code';
        return { data: null, error: errorMessage };
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('💥 Erreur lors de l\'envoi du code de vérification:', error);
      return { data: null, error: error.message || 'Erreur lors de l\'envoi du code' };
    }
  }

  /**
   * Vérifie le code de vérification de connexion et finalise la connexion
   */
  static async verifyLoginCode(email: string, code: string, password: string) {
    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/verify_login_code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_CONFIG.ANON_KEY,
        },
        body: JSON.stringify({ email, code, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Code invalide ou expiré');
      }

      // Si la vérification réussit, la session est retournée par l'Edge Function
      // On doit la stocker dans Supabase
      if (data.session) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (sessionError) {
          throw new Error('Erreur lors de la création de la session');
        }
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('Erreur lors de la vérification du code:', error);
      return { data: null, error: error.message || 'Code invalide ou expiré' };
    }
  }

  /**
   * Marque l'acceptation des conditions générales pour un utilisateur
   * Crée le profil s'il n'existe pas encore
   */
  static async acceptTerms(userId: string) {
    try {
      // Utiliser getUser() qui est plus fiable que getSession() après une connexion
      let user = null;
      const { data: { user: getUserResult }, error: userError } = await supabase.auth.getUser();
      
      if (!userError && getUserResult) {
        user = getUserResult;
      } else {
        // Si getUser() échoue, essayer getSession() en dernier recours
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !session.user) {
          throw new Error('Utilisateur non authentifié');
        }
        user = session.user;
      }

      if (!user) {
        throw new Error('Utilisateur non authentifié');
      }

      // Vérifier si le profil existe
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();

      // Si le profil n'existe pas, le créer avec l'acceptation des conditions
      if ((checkError && checkError.code === 'PGRST116') || !existingProfile) {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: user.email || '',
            nom: user.user_metadata?.nom || user.user_metadata?.family_name || '',
            prenom: user.user_metadata?.prenom || user.user_metadata?.given_name || '',
            conditions_acceptees_at: new Date().toISOString(),
          });

        if (insertError) {
          throw new Error(insertError.message || 'Erreur lors de la création du profil');
        }

        return { error: null };
      }

      // Si le profil existe, mettre à jour uniquement conditions_acceptees_at
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ conditions_acceptees_at: new Date().toISOString() })
        .eq('id', userId);

      if (updateError) {
        throw new Error(updateError.message || 'Erreur lors de l\'enregistrement de l\'acceptation des conditions');
      }

      return { error: null };
    } catch (error: any) {
      console.error('Erreur lors de l\'acceptation des conditions:', error);
      return { error: error.message || 'Erreur lors de l\'acceptation des conditions' };
    }
  }

  /**
   * Vérifie si l'utilisateur a accepté les conditions générales
   */
  static async hasAcceptedTerms(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('conditions_acceptees_at')
        .eq('id', userId)
        .maybeSingle(); // Utiliser maybeSingle() au lieu de single() pour gérer le cas où le profil n'existe pas

      // Si le profil n'existe pas (PGRST116), retourner false
      if (error && error.code === 'PGRST116') {
        return false;
      }

      if (error) {
        console.error('Erreur lors de la vérification de l\'acceptation des conditions:', error);
        return false;
      }

      // Si data est null, le profil n'existe pas
      if (!data) {
        return false;
      }

      return data?.conditions_acceptees_at !== null && data?.conditions_acceptees_at !== undefined;
    } catch (error: any) {
      console.error('Erreur lors de la vérification de l\'acceptation des conditions:', error);
      return false;
    }
  }

  /**
   * Initialise les données par défaut pour un nouvel utilisateur
   * 
   * NOTE : Cette fonction utilise SUPABASE_FUNCTIONS_BASE_URL qui est défini dans web/src/config/index.ts
   * et qui pointe déjà vers le VPS self-hosted (https://api.la-saint-quentinoise.fr/functions/v1)
   * Aucune modification nécessaire côté web.
   */
  static async initUserDefaults(userId: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Utilisateur non authentifié');
      }

      // Utilise SUPABASE_FUNCTIONS_BASE_URL qui pointe vers le VPS self-hosted
      const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/init_user_defaults`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'initialisation des données par défaut');
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('Erreur lors de l\'initialisation des données par défaut:', error);
      return { data: null, error: error.message || 'Erreur lors de l\'initialisation des données par défaut' };
    }
  }
}

// Export des fonctions utilitaires
export const {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signOut,
  getSession,
  getCurrentUser,
  resetPassword,
  updatePassword,
  updateProfile,
  deleteAccount,
  isAuthenticated,
  onAuthStateChange,
} = AuthService; 